import { useStrings } from "@1js/localization-components";
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTrigger,
  Text,
  Toaster,
  Tooltip,
  makeStyles,
  useId,
  type DialogOpenChangeData,
  type DialogOpenChangeEvent,
} from "@fluentui/react-components";
import { SettingsRegular } from "@fluentui/react-icons";
import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { useToast } from "../../hooks/toast/useToast";
import useDialog from "../../hooks/useDialog";
import { useFlightContext } from "../../hooks/useFlightContext";
import {
  fetchLinkedPrompts,
  removeLinkedPromptAsync,
  type LinkedPrompt,
} from "../../services/prompt-client.web";
import useTelemetry from "../../utils/Telemetry/useTelemetry";
import { ConfirmationDialog } from "./ConfirmationDialog";
import {
  FailureStopShareToastMessage,
  ManageAccessDialogTitle,
  SuccessStopShareToastMessage,
} from "./ManageSharedPrompts.strings";
import { SharedPromptsDialog } from "./SharedPromptsDialog.web";

const useStyles = makeStyles({
  text: {
    fontFamily: "Segoe UI",
    fontSize: "16px",
    fontStyle: "normal",
    fontWeight: "400",
    lineHeight: "137.5%",
    "@media (max-width: 800px)": {
      display: "none",
    },
  },
  dialog: {
    "@media (max-width: 540px)": {
      display: "flex",
      maxWidth: "100vw",
      maxHeight: "90%",
      marginBottom: "0px",
    },
  },
});

export const ManageSharedPromptsButton: React.FC = () => {
  const { isFlightActive } = useFlightContext();
  const strings = useStrings({
    SuccessStopShareToastMessage,
    FailureStopShareToastMessage,
    ManageAccessDialogTitle,
  });
  const styles = useStyles();
  const telemetry = useTelemetry();
  const toasterId = useId("toaster");
  const [vertical, setVertical] = React.useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const { notify: _notify } = useToast(toasterId, 3000);
  const { state, dispatch } = useDialog();

  React.useEffect(() => {
    const notify = (...props: Parameters<typeof _notify>) => {
      const dialog = document.getElementById("dialog-title-id");
      setVertical(dialog?.getBoundingClientRect().top || 108); // 108 is the default value for margin between the top of the page and the dialog.
      _notify(...props);
    };

    if (state.toast === "success") {
      notify("success", strings.SuccessStopShareToastMessage());
      telemetry.sendNotificationActionEvent(
        "UnsharePost",
        200,
        "StopSharingButton"
      );
    } else if (state.toast === "error") {
      notify("error", strings.FailureStopShareToastMessage());
      telemetry.sendNotificationActionEvent(
        "UnsharePost",
        500,
        "StopSharingButton"
      );
    }
  }, [state.toast, telemetry, strings, _notify]);

  React.useEffect(() => {
    const getPrompts = async () => {
      if (
        isFlightActive("isManagePromptLinkEnabled") &&
        searchParams.get("manageAccess") === "open"
      ) {
        dispatch({ type: "OPEN_DIALOG" });
        const serviceHost = window.location.hostname.toString();
        const fetchedPrompts: LinkedPrompt[] = await fetchLinkedPrompts(
          serviceHost,
          telemetry.sendApiActionEvent
        );
        dispatch({ type: "LAUNCH_LIST", payload: fetchedPrompts });
      }
    };
    getPrompts();
  }, [searchParams, dispatch, isFlightActive, telemetry.sendApiActionEvent]);

  const handleDeletePrompt = React.useCallback(
    (prompt: LinkedPrompt) => {
      telemetry.sendSettingsActionEvent("StopSharing", "SharedLinksSetting");
      dispatch({ type: "DELETE_PROMPT", payload: prompt });
    },
    [dispatch, telemetry]
  );

  const handleDeletePromptInit = React.useCallback(async () => {
    telemetry.sendSettingsActionEvent("Save", "SharedLinksSetting");
    dispatch({ type: "DELETE_PROMPT_INIT" });
    try {
      if (!state.activePrompt) {
        throw new Error("Prompt ID is missing");
      }
      const serviceHost = window.location.hostname.toString();

      await removeLinkedPromptAsync(
        serviceHost,
        [state.activePrompt.PromptId],
        telemetry.sendApiActionEvent
      );
      telemetry.sendUnsharePromptEvent(state.activePrompt);

      //once the API call is successful, show toast and call DELETE_PROMPT_SUCCEED
      dispatch({ type: "DELETE_PROMPT_SUCCEED" });
    } catch (err) {
      //if API call fails, show toast and call DELETE_PROMPT_FAILED
      dispatch({ type: "DELETE_PROMPT_FAILED" });
    }
  }, [dispatch, state.activePrompt, telemetry]);

  const cancelDelete = React.useCallback(() => {
    telemetry.sendSettingsActionEvent("Cancel", "SharedLinksSetting");
    dispatch({ type: "DELETE_PROMPT_CANCEL" });
  }, [dispatch, telemetry]);

  const openDialog = () => {
    searchParams.set("manageAccess", "open");
    setSearchParams(searchParams);
  };

  const handleOnDialogOpenChanged = (
    _event: DialogOpenChangeEvent,
    data: DialogOpenChangeData
  ) => {
    if (!data.open) {
      searchParams.delete("manageAccess");
      setSearchParams(searchParams);
      dispatch({ type: "CLOSE_LIST" });
    }

    if (data.open) {
      telemetry.sendSettingsActionEvent("Open", "SharedLinksSetting");
    }
  };

  return (
    <>
      <Toaster
        toasterId={toasterId}
        offset={{ vertical: vertical }}
        pauseOnHover
        pauseOnWindowBlur
      />
      <Dialog open={state.open} onOpenChange={handleOnDialogOpenChanged}>
        <DialogTrigger>
          <Tooltip
            content={strings.ManageAccessDialogTitle()}
            relationship="label"
          >
            <Button
              appearance="transparent"
              icon={<SettingsRegular />}
              onClick={openDialog}
            >
              <Text className={styles.text}>
                {strings.ManageAccessDialogTitle()}
              </Text>
            </Button>
          </Tooltip>
        </DialogTrigger>
        <DialogSurface className={styles.dialog}>
          {state.surface === "list" ? (
            <SharedPromptsDialog
              onDeletePrompt={handleDeletePrompt}
              prompts={state.linkedPrompts}
              isLoading={state.status === "loading"}
            />
          ) : (
            <ConfirmationDialog
              deletePrompt={handleDeletePromptInit}
              cancelDelete={cancelDelete}
              isLoading={state.status === "loading"}
            />
          )}
        </DialogSurface>
      </Dialog>
    </>
  );
};
